import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { fileToDataUrl } from "@/lib/format";
import { useAuth } from "@/contexts/AuthContext";
import AdminNav from "@/components/AdminNav";
import "@/styles/portfolio.css";
import "@/styles/admin.css";

const EMPTY = {
  name: "",
  image: "",
  filename: "",
  alt_text: "",
  link: "",
  order: 0,
  published: true,
};

export default function AdminLogos() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [imgBusy, setImgBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/logos");
      setItems(data);
      setError("");
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const startNew = () => {
    setEditing("new");
    setForm(EMPTY);
  };

  const startEdit = (it) => {
    setEditing(it.id);
    setForm({
      name: it.name || "",
      image: it.image || "",
      filename: it.filename || "",
      alt_text: it.alt_text || "",
      link: it.link || "",
      order: it.order || 0,
      published: it.published !== false,
    });
  };

  const cancel = () => {
    setEditing(null);
    setForm(EMPTY);
    setError("");
  };

  const handleImage = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setImgBusy(true);
    setError("");
    try {
      const dataUrl = await fileToDataUrl(f, 800, 0.92);
      setForm((prev) => ({
        ...prev,
        image: dataUrl,
        filename: prev.filename || f.name,
      }));
    } catch (err) {
      setError("Bild konnte nicht verarbeitet werden.");
    } finally {
      setImgBusy(false);
      e.target.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (editing === "new") {
        await api.post("/admin/logos", form);
      } else {
        await api.patch(`/admin/logos/${editing}`, form);
      }
      await load();
      cancel();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (it) => {
    if (!window.confirm(`Logo „${it.name}" löschen?`)) return;
    try {
      await api.delete(`/admin/logos/${it.id}`);
      await load();
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="admin-shell" data-testid="admin-logos">
      <header className="admin-header">
        <div>
          <span className="eyebrow">ADMIN · KUNDENLOGOS</span>
          <h1 className="display admin-h1">
            Logo <span className="italic">Banner</span>.
          </h1>
        </div>
        <div className="admin-header__actions">
          <button type="button" onClick={startNew} className="btn btn--solid" data-testid="logo-new">
            <span>Neues Logo</span>
            <span className="btn__arrow">→</span>
          </button>
          <button type="button" onClick={handleLogout} className="btn btn--ghost">
            <span>Abmelden</span>
          </button>
        </div>
      </header>

      <AdminNav active="/admin/logos" />

      {error && <div className="admin-error">{error}</div>}

      {editing && (
        <form onSubmit={submit} className="admin-form" data-testid="logo-form">
          <div className="admin-field">
            <span className="admin-label">Logo-Bild</span>
            {form.image ? (
              <div className="admin-cover-preview">
                <img src={form.image} alt={form.alt_text || form.name} />
                <div className="admin-cover-actions">
                  <label className="admin-link admin-link--accent">
                    Ersetzen
                    <input type="file" accept="image/*" onChange={handleImage} hidden />
                  </label>
                  <button
                    type="button"
                    className="admin-link admin-link--danger"
                    onClick={() => setForm({ ...form, image: "" })}
                  >
                    Entfernen
                  </button>
                </div>
              </div>
            ) : (
              <label className="admin-upload">
                <input type="file" accept="image/*" onChange={handleImage} hidden data-testid="logo-image-input" />
                <span className="admin-upload__plus">+</span>
                <span className="admin-upload__label">
                  {imgBusy ? "Verarbeitet…" : "Logo hochladen"}
                </span>
                <span className="admin-upload__hint">PNG/SVG · transparent bevorzugt</span>
              </label>
            )}
          </div>

          <label className="admin-field">
            <span className="admin-label">Kundenname</span>
            <input
              type="text"
              className="admin-input"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              placeholder="z. B. Restaurant Sonne"
              data-testid="logo-name"
            />
          </label>

          <label className="admin-field">
            <span className="admin-label">
              Dateiname <span className="admin-hint">(SEO — wird im img-Tag verwendet)</span>
            </span>
            <input
              type="text"
              className="admin-input"
              value={form.filename}
              onChange={(e) => setForm({ ...form, filename: e.target.value })}
              placeholder="logo-restaurant-sonne.png"
              data-testid="logo-filename"
            />
          </label>

          <label className="admin-field">
            <span className="admin-label">
              Alt-Text <span className="admin-hint">(SEO + Barrierefreiheit)</span>
            </span>
            <input
              type="text"
              className="admin-input"
              value={form.alt_text}
              onChange={(e) => setForm({ ...form, alt_text: e.target.value })}
              placeholder="Logo Restaurant Sonne — Kunde von content.by.max"
              data-testid="logo-alt"
            />
          </label>

          <label className="admin-field">
            <span className="admin-label">Link (optional)</span>
            <input
              type="url"
              className="admin-input"
              value={form.link}
              onChange={(e) => setForm({ ...form, link: e.target.value })}
              placeholder="https://www.restaurant-sonne.at"
              data-testid="logo-link"
            />
          </label>

          <label className="admin-field">
            <span className="admin-label">Reihenfolge</span>
            <input
              type="number"
              className="admin-input"
              value={form.order}
              onChange={(e) => setForm({ ...form, order: Number(e.target.value) })}
              data-testid="logo-order"
            />
          </label>

          <label className="admin-toggle">
            <input
              type="checkbox"
              checked={form.published}
              onChange={(e) => setForm({ ...form, published: e.target.checked })}
            />
            <span>Veröffentlicht (im Banner sichtbar)</span>
          </label>

          <div className="admin-form-foot">
            <button type="button" className="btn btn--ghost" onClick={cancel}>
              <span>Abbrechen</span>
            </button>
            <button type="submit" disabled={busy} className="btn btn--solid" data-testid="logo-save">
              <span>{busy ? "Speichert…" : "Speichern"}</span>
              <span className="btn__arrow">→</span>
            </button>
          </div>
        </form>
      )}

      {!editing && (
        <section className="admin-posts">
          {loading && <p className="admin-empty">Lädt…</p>}
          {!loading && items.length === 0 && (
            <p className="admin-empty">Noch keine Logos — lade dein erstes Kunden-Logo hoch.</p>
          )}
          <ul className="admin-post-list" data-testid="logo-list">
            {items.map((it) => (
              <li key={it.id} className="admin-post-row" data-testid={`logo-${it.id}`}>
                <div className="admin-post-row__main" style={{ flexDirection: "row", alignItems: "center", gap: 24, display: "flex" }}>
                  {it.image ? (
                    <img
                      src={it.image}
                      alt={it.alt_text || it.name}
                      style={{ height: 60, width: "auto", maxWidth: 140, objectFit: "contain", background: "#fff", padding: 8, borderRadius: 2 }}
                    />
                  ) : (
                    <span className="admin-empty" style={{ padding: 0, fontSize: 12 }}>(kein Bild)</span>
                  )}
                  <div>
                    <h2 className="admin-post-row__title">{it.name}</h2>
                    <span className="admin-hint">
                      {it.filename || "—"} · {it.published ? "veröffentlicht" : "Entwurf"} · Order {it.order}
                    </span>
                    {it.alt_text && (
                      <p className="admin-post-row__excerpt">Alt: {it.alt_text}</p>
                    )}
                  </div>
                </div>
                <div className="admin-post-row__actions">
                  <button className="admin-link admin-link--accent" onClick={() => startEdit(it)}>
                    Bearbeiten
                  </button>
                  <button className="admin-link admin-link--danger" onClick={() => remove(it)}>
                    Löschen
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      <Link to="/admin" className="admin-back" style={{ marginTop: 32 }}>
        ← Zurück zur Übersicht
      </Link>
    </div>
  );
}
